/**
 * State-Based Actions data for the MTG Rules Reference.
 * Defines all common SBAs with rules references, descriptions,
 * and example scenarios.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/sbaData
 */

const SBA_CATEGORIES = {
  PLAYER: 'Player',
  CREATURE: 'Creature',
  PERMANENT: 'Permanent',
  COUNTER: 'Counter',
};

const SBA_IDS = {
  LIFE_ZERO: 'life-zero',
  DRAW_EMPTY_LIBRARY: 'draw-empty-library',
  POISON_COUNTERS: 'poison-counters',
  TOUGHNESS_ZERO: 'toughness-zero',
  LETHAL_DAMAGE: 'lethal-damage',
  DEATHTOUCH_DAMAGE: 'deathtouch-damage',
  LEGENDARY_RULE: 'legendary-rule',
  PLANESWALKER_LOYALTY_ZERO: 'planeswalker-loyalty-zero',
  TOKEN_WRONG_ZONE: 'token-wrong-zone',
  AURA_ILLEGAL: 'aura-illegal',
  EQUIPMENT_ILLEGAL: 'equipment-illegal',
  PLUS_MINUS_COUNTERS: 'plus-minus-counters',
};

const STATE_BASED_ACTIONS = [
  {
    id: SBA_IDS.LIFE_ZERO,
    name: 'Life Total 0 or Less',
    description:
      'A player with 0 or less life loses the game. This is checked whenever any player would receive priority.',
    rulesReference: '704.5a',
    category: SBA_CATEGORIES.PLAYER,
    examples: [
      {
        scenario: 'A player at 3 life is dealt 5 damage by Lightning Bolt and Shock resolving.',
        resolution: 'Their life total becomes -2 and they lose the game as an SBA before anyone receives priority.',
      },
      {
        scenario: 'A player pays 4 life to activate Greed while at 2 life.',
        resolution: 'They go to -2 life and lose the game as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.DRAW_EMPTY_LIBRARY,
    name: 'Drawing from an Empty Library',
    description:
      'A player who has attempted to draw a card from an empty library since the last time SBAs were checked loses the game. The draw attempt itself does not cause the loss. The SBA check does.',
    rulesReference: '704.5b',
    category: SBA_CATEGORIES.PLAYER,
    examples: [
      {
        scenario: 'A player with no cards in their library casts Brainstorm and attempts to draw the first card.',
        resolution: 'The draw fails. The next time SBAs are checked, that player loses the game.',
      },
      {
        scenario: 'A player uses Thought Scour targeting themselves with only 1 card in library. They mill 2, then attempt to draw from an empty library.',
        resolution: 'They lose the game as an SBA the next time state-based actions are checked.',
      },
    ],
  },
  {
    id: SBA_IDS.POISON_COUNTERS,
    name: 'Ten or More Poison Counters',
    description:
      'A player with 10 or more poison counters loses the game. Poison counters are primarily given by creatures with infect or toxic.',
    rulesReference: '704.5c',
    category: SBA_CATEGORIES.PLAYER,
    examples: [
      {
        scenario: 'A player with 8 poison counters is dealt combat damage by a 3/3 creature with infect.',
        resolution: 'They receive 3 more poison counters (totaling 11) and lose the game as an SBA.',
      },
      {
        scenario: 'Ichor Rats enters the battlefield, giving each player a poison counter. A player already had 9 poison counters.',
        resolution: 'That player now has 10 poison counters and loses the game as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.TOUGHNESS_ZERO,
    name: 'Creature Toughness 0 or Less',
    description:
      'A creature with toughness 0 or less is put into its owner\'s graveyard. This is not destruction. It cannot be prevented by indestructible or regeneration.',
    rulesReference: '704.5f',
    category: SBA_CATEGORIES.CREATURE,
    examples: [
      {
        scenario: 'A 2/2 creature gets -2/-2 from an effect, reducing its toughness to 0.',
        resolution: 'It is put into the graveyard as an SBA. Indestructible would not save it.',
      },
      {
        scenario: 'A creature with three -1/-1 counters has a base toughness of 3.',
        resolution: 'Its toughness is 0 and it is put into the graveyard as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.LETHAL_DAMAGE,
    name: 'Lethal Damage',
    description:
      'A creature that has been dealt damage equal to or greater than its toughness is destroyed. This is destruction and can be prevented by indestructible.',
    rulesReference: '704.5g',
    category: SBA_CATEGORIES.CREATURE,
    examples: [
      {
        scenario: 'A 4/4 creature is dealt 4 damage by Lightning Bolt and Shock in the same turn.',
        resolution: 'It has lethal damage marked on it and is destroyed as an SBA.',
      },
      {
        scenario: 'A 4/4 indestructible creature is dealt 10 damage.',
        resolution: 'It is not destroyed because indestructible prevents destruction from lethal damage. The damage is still marked on it.',
      },
    ],
  },
  {
    id: SBA_IDS.DEATHTOUCH_DAMAGE,
    name: 'Deathtouch Damage',
    description:
      'A creature that has been dealt damage by a source with deathtouch since the last time SBAs were checked is destroyed. Any amount of damage from a deathtouch source is considered lethal.',
    rulesReference: '704.5h',
    category: SBA_CATEGORIES.CREATURE,
    examples: [
      {
        scenario: 'A 1/1 creature with deathtouch deals 1 damage to a 10/10 creature.',
        resolution: 'The 10/10 is destroyed as an SBA because it was dealt damage by a source with deathtouch.',
      },
      {
        scenario: 'A creature with deathtouch and 0 power assigns 0 combat damage to a blocker.',
        resolution: 'Zero damage is not "damage dealt," so the blocker is NOT destroyed by deathtouch.',
      },
    ],
  },
  {
    id: SBA_IDS.LEGENDARY_RULE,
    name: 'Legend Rule',
    description:
      'If a player controls two or more legendary permanents with the same name, that player chooses one and puts the rest into the graveyard. This is not destruction. It bypasses indestructible.',
    rulesReference: '704.5j',
    category: SBA_CATEGORIES.PERMANENT,
    examples: [
      {
        scenario: 'A player controls Sheoldred, the Apocalypse and casts a second copy.',
        resolution: 'When SBAs are checked, that player chooses one to keep and puts the other into the graveyard.',
      },
      {
        scenario: 'A player uses Clone to copy an opponent\'s legendary creature.',
        resolution: 'The legend rule only applies to permanents one player controls, so only the Clone player would need to choose if they now control two legendary permanents with the same name.',
      },
    ],
  },
  {
    id: SBA_IDS.PLANESWALKER_LOYALTY_ZERO,
    name: 'Planeswalker with 0 Loyalty',
    description:
      'A planeswalker with 0 or fewer loyalty counters is put into its owner\'s graveyard. Planeswalkers lose loyalty from damage dealt to them and from activating minus loyalty abilities.',
    rulesReference: '704.5i',
    category: SBA_CATEGORIES.PERMANENT,
    examples: [
      {
        scenario: 'Jace, the Mind Sculptor enters with 3 loyalty and activates his -1 ability twice across two turns, going to 1 loyalty. An opponent deals 1 damage to Jace.',
        resolution: 'The last loyalty counter is removed. Jace is put into the graveyard as an SBA.',
      },
      {
        scenario: 'A planeswalker with 4 loyalty is dealt 6 damage by an attacking creature.',
        resolution: 'It goes to 0 loyalty (it cannot go below 0) and is put into the graveyard as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.TOKEN_WRONG_ZONE,
    name: 'Token in a Non-Battlefield Zone',
    description:
      'A token that is in any zone other than the battlefield ceases to exist. This happens after any zone-change triggers have had a chance to trigger. Tokens can momentarily exist in other zones (e.g., a token dying goes to the graveyard, triggering "dies" abilities, then ceases to exist).',
    rulesReference: '704.5d',
    category: SBA_CATEGORIES.PERMANENT,
    examples: [
      {
        scenario: 'A creature token is destroyed and moves to the graveyard.',
        resolution: '"Whenever a creature dies" triggers fire. Then, as an SBA, the token ceases to exist in the graveyard.',
      },
      {
        scenario: 'A token creature is bounced to its owner\'s hand by Unsummon.',
        resolution: 'The token briefly goes to the hand, then ceases to exist as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.AURA_ILLEGAL,
    name: 'Unattached or Illegal Aura',
    description:
      'An Aura that is not attached to a legal object or player, or is attached to an object or player it can\'t legally enchant, is put into its owner\'s graveyard.',
    rulesReference: '704.5m',
    category: SBA_CATEGORIES.PERMANENT,
    examples: [
      {
        scenario: 'A creature enchanted by Pacifism is sacrificed. Pacifism is no longer attached to anything.',
        resolution: 'Pacifism is put into the graveyard as an SBA.',
      },
      {
        scenario: 'An Aura with "enchant creature with power 3 or less" is on a 2/2 creature that receives +2/+0, making it 4/2.',
        resolution: 'The Aura is now illegally attached and is put into the graveyard as an SBA.',
      },
    ],
  },
  {
    id: SBA_IDS.EQUIPMENT_ILLEGAL,
    name: 'Illegal Equipment or Fortification',
    description:
      'An Equipment or Fortification that is attached to an illegal permanent becomes unattached (but remains on the battlefield). This differs from Auras, which go to the graveyard.',
    rulesReference: '704.5n',
    category: SBA_CATEGORIES.PERMANENT,
    examples: [
      {
        scenario: 'A creature equipped with Sword of Fire and Ice is turned into a noncreature artifact by an effect.',
        resolution: 'The Equipment becomes unattached and remains on the battlefield.',
      },
      {
        scenario: 'An Equipment that says "Equip legendary creature" is attached to a creature that loses its legendary supertype.',
        resolution: 'The Equipment becomes unattached but stays on the battlefield.',
      },
    ],
  },
  {
    id: SBA_IDS.PLUS_MINUS_COUNTERS,
    name: '+1/+1 and -1/-1 Counter Cancellation',
    description:
      'If a permanent has both +1/+1 counters and -1/-1 counters on it, pairs of +1/+1 and -1/-1 counters are removed until only one type remains. This happens simultaneously with other SBAs.',
    rulesReference: '704.5q',
    category: SBA_CATEGORIES.COUNTER,
    examples: [
      {
        scenario: 'A creature has three +1/+1 counters and two -1/-1 counters.',
        resolution: 'Two pairs cancel out, leaving the creature with one +1/+1 counter.',
      },
      {
        scenario: 'A creature with undying returns to the battlefield with a +1/+1 counter, then receives a -1/-1 counter from a Wither source.',
        resolution: 'The counters cancel each other out, leaving the creature with no counters, meaning undying can trigger again if it dies.',
      },
    ],
  },
];

module.exports = {
  SBA_IDS,
  SBA_CATEGORIES,
  STATE_BASED_ACTIONS,
};
